import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi } from '../api';
import { useAuth } from '../auth/useAuth';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { RateLimitBanner } from './RateLimitBanner';
import { useLoginFlow } from './useLoginFlow';

export function MfaChallengePage() {
  const { state, dispatch } = useLoginFlow();
  const { authenticate } = useAuth();
  const navigate = useNavigate();

  const [code, setCode] = useState('');
  const [isRecovery, setIsRecovery] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Guard: must arrive here via the credentials flow.
  useEffect(() => {
    if (state.step !== 'mfa_challenge') {
      void navigate('/login', { replace: true });
    }
  }, [state.step, navigate]);

  if (state.step !== 'mfa_challenge') return null;

  const isRateLimited = (state.rateLimitUntil ?? 0) > Date.now();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await authApi.loginMfa({
        challengeToken: state.challengeToken,
        totpCode: code,
        isRecoveryCode: isRecovery,
      });

      const { accessToken, refreshToken, expiresAt, user } = result;
      if (!accessToken || !refreshToken || !expiresAt) throw new Error('Incomplete response');

      dispatch({ type: 'AUTHENTICATED' });
      authenticate({ accessToken, refreshToken, expiresAt }, user ?? null);
      void navigate('/', { replace: true });
    } catch (err: unknown) {
      const res = (err as { response?: { status?: number; headers?: Record<string, string> } }).response;
      if (res?.status === 429) {
        const retryAfter = parseInt(res.headers?.['retry-after'] ?? '60', 10);
        dispatch({ type: 'RATE_LIMITED', retryAfterSeconds: retryAfter });
      } else if (res?.status === 401) {
        setError(isRecovery ? 'Invalid recovery code.' : 'Invalid code. Try again.');
      } else {
        setError('An error occurred. Try again.');
      }
    } finally {
      setLoading(false);
      setCode('');
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', lineHeight: 'var(--leading-normal)' }}>
        {isRecovery
          ? 'Enter one of your recovery codes.'
          : 'Enter the 6-digit code from your authenticator app.'}
      </p>

      {isRateLimited && state.rateLimitUntil && (
        <RateLimitBanner untilMs={state.rateLimitUntil} />
      )}

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Input
          id="mfa-code"
          label={isRecovery ? 'Recovery code' : 'Authentication code'}
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value.trim())}
          maxLength={isRecovery ? undefined : 6}
          inputMode={isRecovery ? 'text' : 'numeric'}
          pattern={isRecovery ? undefined : '\\d{6}'}
          autoComplete="one-time-code"
          required
          disabled={isRateLimited}
          style={{ fontFamily: 'monospace', letterSpacing: '0.15em', textAlign: 'center' }}
        />

        {error && (
          <div
            style={{
              background: 'var(--badge-rejected-bg)',
              border: '1px solid var(--badge-rejected-text)',
              borderRadius: 'var(--radius-md)',
              padding: '10px 14px',
              fontSize: 'var(--text-sm)',
              color: 'var(--badge-rejected-text)',
            }}
          >
            {error}
          </div>
        )}

        <Button
          type="submit"
          variant="primary"
          size="lg"
          loading={loading}
          disabled={isRateLimited}
          style={{ width: '100%' }}
        >
          {loading ? 'Verifying…' : 'Verify'}
        </Button>
      </form>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
        <button
          type="button"
          onClick={() => { setIsRecovery((v) => !v); setCode(''); setError(''); }}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            fontSize: 'var(--text-sm)',
            color: 'var(--text-brand)',
            cursor: 'pointer',
            textAlign: 'left',
          }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.textDecoration = 'underline'; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.textDecoration = 'none'; }}
        >
          {isRecovery ? 'Use authenticator code' : 'Use recovery code'}
        </button>

        <button
          type="button"
          onClick={() => void navigate('/login', { replace: true })}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            fontSize: 'var(--text-sm)',
            color: 'var(--text-tertiary)',
            cursor: 'pointer',
            textAlign: 'left',
          }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = 'var(--text-secondary)'; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = 'var(--text-tertiary)'; }}
        >
          ← Back to sign in
        </button>
      </div>
    </div>
  );
}
